import { useCallback, useMemo, useState } from "react"
import { RightArrow } from "./svgCollection"

type Props = {
    value?: string
    min?: string
    max?: string

    open: boolean
    onClose: () => void
    onSelect: (date: string) => void
}

const locale = "ru-RU"

/* ================= utils ================= */

const toISO = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

const clampDate = (d: Date, min?: Date | null, max?: Date | null) => {
    if (max && d > max) return max
    if (min && d < min) return min
    return d
}

/* ================= component ================= */

export default function Calendar({ value, min, max, open, onSelect, onClose }: Props) {
    if (!open) return null

    /* ---------- dates ---------- */

    const today = useMemo(() => new Date(), [])
    const todayISO = useMemo(() => toISO(today), [today])
    const getDefaultMaxDate = (today: Date) => new Date(today.getFullYear() - 14, today.getMonth(), today.getDate())

    const minDate = useMemo(() => (min ? new Date(min) : null), [min])
    const maxDate = useMemo(() => (max ? new Date(max) : getDefaultMaxDate(today)), [max, today])

    const initialDate = useMemo(() => {
        const base = value ? new Date(value) : today
        return clampDate(base, minDate, maxDate)
    }, [value, today, minDate, maxDate])

    /* ---------- state ---------- */

    const [year, setYear] = useState(() => initialDate.getFullYear())
    const [month, setMonth] = useState(() => initialDate.getMonth())
    const [yearOpen, setYearOpen] = useState(false)

    /* ---------- memo ---------- */

    const monthLabel = useMemo(
        () =>
            `${new Date(year, month).toLocaleString(locale, {
                month: "long"
            })}, ${year}`,
        [year, month]
    )

    const weekDays = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" })
        return Array.from(
            { length: 7 },
            (_, i) => formatter.format(new Date(1970, 0, 5 + i)) // понедельник
        )
    }, [])

    const years = useMemo(() => {
        const from = minDate?.getFullYear() || year - 100
        const to = maxDate.getFullYear()

        return Array.from({ length: to - from + 1 }, (_, i) => to - i)
    }, [minDate, maxDate])

    const days = useMemo(() => {
        const firstDay = new Date(year, month, 1)
        const offset = (firstDay.getDay() + 6) % 7
        const total = new Date(year, month + 1, 0).getDate()

        return Array.from({ length: offset + total }, (_, i) => (i < offset ? null : new Date(year, month, i - offset + 1)))
    }, [year, month])

    /* ---------- callbacks ---------- */

    const isDisabled = useCallback((d: Date) => (minDate && d < minDate) || (maxDate && d > maxDate), [minDate, maxDate])

    const changeMonth = useCallback(
        (delta: number) => {
            const d = clampDate(new Date(year, month + delta, 1), minDate, maxDate)
            setYear(d.getFullYear())
            setMonth(d.getMonth())
            setYearOpen(false)
        },
        [year, month, minDate, maxDate]
    )

    /* ---------- render ---------- */

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]" onClick={onClose}>
            <div className="relative" onClick={e => e.stopPropagation()}>
                <div className="w-80 rounded-xl bg-neutral-900 p-4 text-white" onClick={() => setYearOpen(false)}>
                    {/* ===== Header ===== */}
                    <div className="mb-4 flex items-center justify-between">
                        <button type="button" className="rotate-180 cursor-pointer" onClick={() => changeMonth(-1)}>
                            <RightArrow />
                        </button>

                        <div
                            className="relative flex cursor-pointer items-center"
                            onClick={e => {
                                e.stopPropagation()
                                setYearOpen(v => !v)
                            }}>
                            <span className="text-lg font-medium capitalize">{monthLabel}</span>

                            {yearOpen && (
                                <div
                                    className="absolute top-full left-1/2 z-20 mt-2 max-h-48 w-28 -translate-x-1/2 overflow-auto rounded-md border border-white/10 bg-neutral-800 shadow"
                                    onClick={e => e.stopPropagation()}>
                                    {years.map(y => (
                                        <button
                                            key={y}
                                            type="button"
                                            onClick={() => {
                                                setYear(y)
                                                setYearOpen(false)
                                            }}
                                            className={[
                                                "flex w-full cursor-pointer justify-center px-3 py-1 text-left text-sm hover:bg-white/10",
                                                y === year && "bg-white/10"
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}>
                                            {y}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button type="button" className="cursor-pointer" onClick={() => changeMonth(1)}>
                            <RightArrow />
                        </button>
                    </div>

                    {/* ===== Weekdays ===== */}
                    <div className="mb-2 grid grid-cols-7 text-center text-sm text-white/50">
                        {weekDays.map(d => (
                            <div key={d}>{d}</div>
                        ))}
                    </div>

                    {/* ===== Days ===== */}
                    <div className="grid grid-cols-7 gap-1 text-center">
                        {days.map((date, i) => {
                            if (!date) return <div key={i} />

                            const iso = toISO(date)
                            const selected = value === iso
                            const isToday = iso === todayISO
                            const disabled = isDisabled(date)

                            return (
                                <button
                                    key={iso}
                                    disabled={!!disabled}
                                    onClick={() => {
                                        if (disabled || yearOpen) return
                                        onSelect(iso)
                                        onClose()
                                    }}
                                    className={[
                                        "h-9 cursor-pointer rounded-full transition",
                                        disabled && "cursor-not-allowed text-white/30",
                                        selected && "bg-blue-500 text-black",
                                        !selected && isToday && "ring-2 ring-white/60",
                                        !disabled && !selected && "hover:bg-white/10"
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}>
                                    {date.getDate()}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}
